import type { Tier } from '../types'

interface SparklineProps {
  // Points on a 0..100 scale. The component normalizes them to the SVG viewport.
  points: number[]
  width?: number
  height?: number
  tier?: Tier | null
  className?: string
}

// Tier stroke color map. T1 emerald, T2 sky, T3 amber, T4 red.
// Mirrors the tier badge palette used elsewhere on the leaderboard so an
// agent's row reads with one consistent color identity.
const TIER_STROKE: Record<Tier, string> = {
  1: '#34d399', // emerald-400
  2: '#38bdf8', // sky-400
  3: '#fbbf24', // amber-400
  4: '#f87171', // red-400
}

const FALLBACK_STROKE = '#64748b' // slate-500

export function Sparkline({
  points,
  width = 80,
  height = 24,
  tier,
  className = '',
}: SparklineProps) {
  // Render a faint dash placeholder when there's nothing to plot.
  if (!points || points.length === 0) {
    return <span className={`text-xs text-slate-600 ${className}`}>--</span>
  }

  // Single point — render a tiny dot rather than an empty polyline.
  const stroke = tier ? TIER_STROKE[tier] : FALLBACK_STROKE
  const padX = 2
  const padY = 2
  const innerW = Math.max(1, width - padX * 2)
  const innerH = Math.max(1, height - padY * 2)

  const minVal = 0
  const maxVal = 100
  const range = maxVal - minVal || 1

  const xFor = (i: number, n: number) =>
    n <= 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW
  const yFor = (v: number) => {
    const clamped = Math.min(maxVal, Math.max(minVal, v))
    // Higher value -> closer to the top of the SVG.
    return padY + innerH - ((clamped - minVal) / range) * innerH
  }

  if (points.length === 1) {
    return (
      <svg
        className={className}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
      >
        <circle cx={xFor(0, 1)} cy={yFor(points[0])} r={1.5} fill={stroke} />
      </svg>
    )
  }

  const d = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i, points.length).toFixed(2)} ${yFor(v).toFixed(2)}`)
    .join(' ')

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
